import React from 'react';
import { RefreshCw, ArrowRight, Lightbulb, Brain } from 'lucide-react';

const TemplateWizard = ({
  templateData,
  handleTemplateChange,
  templateTypes,
  usefulExpressions,
  resetTemplate,
  setActiveTab
}) => {
  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div 
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-green-600 to-blue-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(34, 197, 94, 0.9), rgba(59, 130, 246, 0.9)), url(https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-3">Template Wizard</h2>
          <p className="text-green-100 text-lg">
            Create your perfect letter or email with our guided wizard
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Build Your Template</h3>
            <p className="text-gray-600 mt-1">Fill in the details below to create your professional letter or email</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={resetTemplate}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={() => setActiveTab('flashcards')}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors group"
            >
              <Brain className="w-4 h-4" />
              <span>Flash Cards</span>
            </button>
            <button
              onClick={() => setActiveTab('generated')}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors group"
            >
              <span>Generate Essay</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
        
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left Column - Basic Information */}
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-blue-900 mb-4">Basic Information</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Type</label>
                  <select
                    value={templateData.type}
                    onChange={(e) => handleTemplateChange('type', e.target.value)}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  >
                    {templateTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recipient</label>
                  <input
                    type="text"
                    value={templateData.recipient}
                    onChange={(e) => handleTemplateChange('recipient', e.target.value)}
                    placeholder="To whom you're writing"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sender</label>
                  <input
                    type="text"
                    value={templateData.sender}
                    onChange={(e) => handleTemplateChange('sender', e.target.value)}
                    placeholder="Your name and title"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <input
                      type="text"
                      value={templateData.date}
                      onChange={(e) => handleTemplateChange('date', e.target.value)}
                      placeholder="Date of writing"
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <input
                      type="text"
                      value={templateData.subject}
                      onChange={(e) => handleTemplateChange('subject', e.target.value)}
                      placeholder="Subject line"
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-green-900 mb-4">Content Sections</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-blue-600 mb-2">Greeting/Salutation</label>
                  <input
                    type="text"
                    value={templateData.greeting}
                    onChange={(e) => handleTemplateChange('greeting', e.target.value)}
                    placeholder="Hi Julie / Dear Mr. Smith"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-green-600 mb-2">Opening</label>
                  <textarea
                    value={templateData.opening}
                    onChange={(e) => handleTemplateChange('opening', e.target.value)}
                    placeholder="Opening paragraph - reason for writing"
                    rows="3"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-600 mb-2">Main Body</label>
                  <textarea
                    value={templateData.mainBody}
                    onChange={(e) => handleTemplateChange('mainBody', e.target.value)}
                    placeholder="Main content - details, explanations, arguments"
                    rows="6"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-orange-600 mb-2">Conclusion</label>
                  <textarea
                    value={templateData.conclusion}
                    onChange={(e) => handleTemplateChange('conclusion', e.target.value)}
                    placeholder="Concluding paragraph - final thoughts, next steps"
                    rows="3"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-red-600 mb-2">Closing</label>
                  <textarea
                    value={templateData.closing}
                    onChange={(e) => handleTemplateChange('closing', e.target.value)}
                    placeholder="Regards, Yours sincerely, etc."
                    rows="2"
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mt-8 bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Template Completion</span>
            <span className="text-sm text-gray-600">
              {Object.values(templateData).filter(value => value.trim() !== '').length}/8 fields completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${(Object.values(templateData).filter(value => value.trim() !== '').length / 8) * 100}%` 
              }}
            ></div>
          </div>
        </div>

        {/* Useful Expressions */}
        {usefulExpressions[templateData.type] && (
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                  Useful Expressions for {templateTypes.find(t => t.value === templateData.type)?.label}
                </h3>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {usefulExpressions[templateData.type].map((expression, index) => (
                    <div 
                      key={index} 
                      className="text-sm text-yellow-800 bg-yellow-100 p-3 rounded-lg border border-yellow-200 hover:bg-yellow-200 transition-colors cursor-pointer"
                      onClick={() => {
                        // Auto-suggest functionality - add to current field focus
                        const activeElement = document.activeElement;
                        if (activeElement && activeElement.tagName === 'TEXTAREA') {
                          const currentValue = activeElement.value;
                          const newValue = currentValue ? `${currentValue} ${expression}` : expression;
                          const fieldName = activeElement.getAttribute('data-field') || 
                                          (activeElement.placeholder.includes('Opening') ? 'opening' :
                                           activeElement.placeholder.includes('Main') ? 'mainBody' :
                                           activeElement.placeholder.includes('Concluding') ? 'conclusion' : '');
                          if (fieldName) {
                            handleTemplateChange(fieldName, newValue);
                          }
                        }
                      }}
                    >
                      {expression}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-yellow-700 mt-3">
                  💡 Tip: Click on any expression to add it to your current text field
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateWizard;
